import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import OrderForm from './components/OrderForm';
import ExistingOrderForm from './components/ExistingOrderForm';
import ProfileModal from './components/ProfileModal';
import NotificationsPage from './components/NotificationsPage';
import ReviewsPage from './components/ReviewsPage';
import DesignRevisionChat from './components/DesignRevisionChat';
import SurveysPanel from './components/SurveysPanel';
import confetti from 'canvas-confetti';

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  order_title: string;
  order_description: string;
  status: string;
  file_urls: string[];
  created_at: string;
  phone?: string;
  company?: string;
  admin_note?: string;
  approved_design_url?: string;
  tracking_number?: string;
  shipping_company?: string;
  design_approved_by_admin?: boolean;
  design_approved_by_customer?: boolean;
  customer_approval_date?: string;
  price?: number;
  paid?: boolean;
  product_title?: string;
  product_description?: string;
}

interface CartItem {
  id: string;
  order_id: string;
  recommendation_id?: string;
  customer_email: string;
  added_at: string;
}

interface RevisionMessage {
  id: string;
  order_id: string;
  sender_type: 'customer' | 'admin';
  sender_email: string;
  message: string;
  created_at: string;
}

interface Quote {
  id: string;
  admin_email: string;
  title: string;
  description: string;
  items: Array<{
    name: string;
    quantity: number;
    unit_price: number;
    total: number;
  }>;
  total_price: number;
  valid_until: string | null;
  share_id: string;
  customer_email: string | null;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  created_at: string;
  updated_at: string;
}

interface Recommendation {
  id: string;
  admin_email: string;
  title: string;
  description: string;
  price: number;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

// Durum sıralaması - hangi durumların tamamlandığını belirlemek için
const statusOrder = [
  'pending',
  'designer_review', 
  'design_in_progress',
  'awaiting_approval',
  'sent_to_print',
  'shipped'
];

const getStatusIndex = (status: string) => {
  const index = statusOrder.indexOf(status);
  return index === -1 ? -1 : index;
};

export default function CustomerDashboard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showExistingOrderForm, setShowExistingOrderForm] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [showSurveys, setShowSurveys] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvingDesign, setApprovingDesign] = useState(false);
  const [showApprovalSuccess, setShowApprovalSuccess] = useState(false);
  const [showMiniReview, setShowMiniReview] = useState(false);
  const [miniReviewRating, setMiniReviewRating] = useState(0);
  const [miniReviewComment, setMiniReviewComment] = useState('');
  const [savingMiniReview, setSavingMiniReview] = useState(false);
  const [showDeliveryReview, setShowDeliveryReview] = useState(false);
  const [deliveryReviewData, setDeliveryReviewData] = useState({
    generalRating: 0,
    printQualityRating: 0,
    speedRating: 0,
    comment: ''
  });
  const [savingDeliveryReview, setSavingDeliveryReview] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showRevisionChat, setShowRevisionChat] = useState(false);
  const navigate = useNavigate();
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [revisionMessages, setRevisionMessages] = useState<RevisionMessage[]>([]);
  const [showQuotes, setShowQuotes] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [acceptingQuote, setAcceptingQuote] = useState<string | null>(null);

  // Öneriler state
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [addingRecommendationToCart, setAddingRecommendationToCart] = useState<string | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Session'dan kullanıcıyı al
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user?.email) {
          // Email'i state ve localStorage'a kaydet
          setCustomerEmail(session.user.email);
          localStorage.setItem('customerEmail', session.user.email);
          
          // customer_profiles tablosunda kayıt yoksa oluştur
          const { data: profile } = await supabase
            .from('customer_profiles')
            .select('id')
            .eq('customer_email', session.user.email)
            .maybeSingle();
          
          if (!profile) {
            // Profil yoksa oluştur
            await supabase
              .from('customer_profiles')
              .insert({
                customer_email: session.user.email,
                billing_same_as_shipping: true
              });
          }
        } else {
          // Session yoksa login'e yönlendir
          navigate('/customer-login');
          return;
        }
      } catch (error) {
        console.error('Auth init error:', error);
        navigate('/customer-login');
      }
    };

    initAuth();
  }, [navigate]);

  useEffect(() => {
    if (!customerEmail) return;
    
    fetchOrders();
    fetchCartItems();
    fetchUnreadNotifications();
    
    // URL'den ödeme sonucunu kontrol et
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      alert('✅ Ödeme başarılı! Siparişiniz işleme alındı.');
      window.history.replaceState({}, '', '/customer-dashboard');
    } else if (paymentStatus === 'fail') {
      alert('❌ Ödeme başarısız oldu. Lütfen tekrar deneyin.');
      window.history.replaceState({}, '', '/customer-dashboard');
    }
    
    // Gerçek zamanlı güncellemeler için subscription
    const ordersSubscription = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          console.log('Sipariş güncellendi:', payload);
          fetchOrders();
        }
      )
      .subscribe();

    // Bildirimler için subscription
    const notificationsSubscription = supabase
      .channel('notifications-changes-count')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        () => {
          fetchUnreadNotifications();
        }
      )
      .subscribe();

    return () => {
      ordersSubscription.unsubscribe();
      notificationsSubscription.unsubscribe();
    };
  }, [customerEmail, navigate]);

  const fetchUnreadNotifications = async () => {
    if (!customerEmail) return;
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('customer_email', customerEmail)
        .eq('is_read', false);

      if (error) throw error;
      setUnreadNotifications(count || 0);
    } catch (error) {
      console.error('Bildirim sayısı alınırken hata:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('customer_email', customerEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (data) {
        setOrders(data);
        if (selectedOrder) {
          const updatedOrder = data.find(o => o.id === selectedOrder.id);
          if (updatedOrder) {
            setSelectedOrder(updatedOrder);
          }
        } else if (data.length > 0) {
          setSelectedOrder(data[0]);
        }
      }
    } catch (error) {
      console.error('Siparişler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Onaylanan siparişin düzenleme görüşmelerini getir
  const fetchRevisionMessages = async (orderId: string) => {
    try {
      const { data, error } = await supabase
        .from('design_revisions')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      if (data) setRevisionMessages(data);
    } catch (error) {
      console.error('Görüşmeler yüklenirken hata:', error);
      setRevisionMessages([]);
    }
  };

  // selectedOrder değiştiğinde ve tasarım onaylandığında görüşmeleri getir
  useEffect(() => {
    if (selectedOrder && (selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer)) {
      fetchRevisionMessages(selectedOrder.id);
    } else {
      setRevisionMessages([]);
    }
  }, [selectedOrder?.id, selectedOrder?.design_approved_by_admin, selectedOrder?.design_approved_by_customer]);

  const fetchCartItems = async () => {
    try {
      const { data, error } = await supabase
        .from('cart')
        .select('*')
        .eq('customer_email', customerEmail);

      if (error) throw error;
      if (data) {
        setCartItems(data);
      }
    } catch (error) {
      console.error('Sepet yüklenirken hata:', error);
    }
  };

  const isInCart = (orderId: string) => {
    return cartItems.some((item) => item.order_id === orderId);
  };

  const addToCart = async (orderId: string) => {
    if (!customerEmail) return;
    
    setAddingToCart(true);
    try {
      const { error } = await supabase
        .from('cart')
        .insert({
          customer_email: customerEmail,
          order_id: orderId,
        });

      if (error) {
        if (error.code === '23505') {
          // Zaten sepette, direkt sepet sayfasına yönlendir
          navigate('/cart');
        } else {
          throw error;
        }
      } else {
        await fetchCartItems();
        // Sepete eklendikten sonra sepet sayfasına yönlendir
        navigate('/cart');
      }
    } catch (error) {
      console.error('Sepete eklenirken hata:', error);
      alert('Sepete eklenirken bir hata oluştu');
    } finally {
      setAddingToCart(false);
    }
  };

  const goToCart = () => {
    navigate('/cart');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('customerEmail');
    navigate('/customer-login');
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Tasarım Bilgileriniz Bekleniyor',
      designer_review: 'Grafiker Kontrolünde',
      design_in_progress: 'Tasarım Hazırlanıyor',
      awaiting_approval: 'Tasarım Onayınız Bekleniyor',
      sent_to_print: 'Üretimde',
      shipped: 'Ürün Kargolandı',
      cancelled: 'İptal',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-amber-500',
      designer_review: 'bg-indigo-500',
      design_in_progress: 'bg-cyan-500',
      awaiting_approval: 'bg-orange-500',
      sent_to_print: 'bg-teal-500',
      shipped: 'bg-emerald-500',
      cancelled: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusBgColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-700',
      designer_review: 'bg-indigo-100 text-indigo-700',
      design_in_progress: 'bg-cyan-100 text-cyan-700',
      awaiting_approval: 'bg-orange-100 text-orange-700',
      sent_to_print: 'bg-teal-100 text-teal-700',
      shipped: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRevisionTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Durum adımının tamamlanıp tamamlanmadığını kontrol et
  const isStepCompleted = (stepStatus: string, currentStatus: string) => {
    if (currentStatus === 'cancelled') return false;
    const stepIndex = getStatusIndex(stepStatus);
    const currentIndex = getStatusIndex(currentStatus);
    return currentIndex >= stepIndex;
  };

  // Sepete eklenebilir sipariş sayısını hesapla
  const getPaidOrdersCount = () => {
    return orders.filter(order => order.price !== null && order.price !== undefined && order.price > 0 && !order.paid).length;
  };

  const handleApproveDesign = async () => {
    if (!selectedOrder || !customerEmail) return;
    
    setApprovingDesign(true);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'customerApproveDesign',
            orderId: selectedOrder.id,
            customerEmail: customerEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        const approvalDate = new Date().toISOString();
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === selectedOrder.id ? { 
              ...order, 
              design_approved_by_customer: true,
              customer_approval_date: approvalDate
            } : order
          )
        );
        setSelectedOrder(prev => prev ? { 
          ...prev, 
          design_approved_by_customer: true,
          customer_approval_date: approvalDate
        } : null);
        setShowApprovalModal(false);
        
        // Confetti animasyonu
        const duration = 2000;
        const animationEnd = Date.now() + duration;
        const colors = ['#FFD700', '#4169E1', '#FF69B4', '#32CD32', '#FF4500'];

        const randomInRange = (min: number, max: number) => {
          return Math.random() * (max - min) + min;
        };

        const frame = () => {
          confetti({
            particleCount: 3,
            angle: randomInRange(55, 125),
            spread: randomInRange(50, 70),
            origin: { x: 0.5, y: 0.6 },
            colors: colors,
            shapes: ['square', 'circle'],
            gravity: 1,
            scalar: 0.8,
            drift: 0,
            ticks: 200
          });

          if (Date.now() < animationEnd) {
            requestAnimationFrame(frame);
          }
        };

        frame();

        // Başarı mesajını göster
        setShowApprovalSuccess(true);
        setTimeout(() => {
          setShowApprovalSuccess(false);
          setShowMiniReview(true);
        }, 2000);
      }
    } catch (error) {
      console.error('Tasarım onaylanırken hata:', error);
      alert('Tasarım onaylanırken bir hata oluştu');
    } finally {
      setApprovingDesign(false);
    }
  };

  const handleSaveMiniReview = async () => {
    if (!selectedOrder || miniReviewRating === 0) return;

    setSavingMiniReview(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          order_id: selectedOrder.id,
          customer_email: customerEmail,
          customer_name: selectedOrder.customer_name,
          rating: miniReviewRating,
          comment: miniReviewComment.trim() || 'Tasarım onay süreci memnun edici.',
          review_type: 'design_approval',
          design_approval_rating: miniReviewRating
        });

      if (error) throw error;

      setShowMiniReview(false);
      setMiniReviewRating(0);
      setMiniReviewComment('');
    } catch (error) {
      console.error('Değerlendirme kaydedilirken hata:', error);
    } finally {
      setSavingMiniReview(false);
    }
  };

  const handleSkipMiniReview = () => {
    setShowMiniReview(false);
    setMiniReviewRating(0);
    setMiniReviewComment('');
  };

  const handleSaveDeliveryReview = async () => {
    if (!selectedOrder || deliveryReviewData.generalRating === 0) return;

    setSavingDeliveryReview(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          order_id: selectedOrder.id,
          customer_email: customerEmail,
          customer_name: selectedOrder.customer_name,
          rating: deliveryReviewData.generalRating,
          comment: deliveryReviewData.comment.trim() || 'Teslimat başarılı.',
          review_type: 'delivery',
          print_quality_rating: deliveryReviewData.printQualityRating,
          speed_rating: deliveryReviewData.speedRating
        });

      if (error) throw error;

      setShowDeliveryReview(false);
      setDeliveryReviewData({
        generalRating: 0,
        printQualityRating: 0,
        speedRating: 0,
        comment: ''
      });
      alert('✅ Değerlendirmeniz için teşekkür ederiz!');
    } catch (error) {
      console.error('Değerlendirme kaydedilirken hata:', error);
      alert('Değerlendirme kaydedilirken bir hata oluştu');
    } finally {
      setSavingDeliveryReview(false);
    }
  };

  // Teslim edilmiş siparişler için değerlendirme kontrolü
  useEffect(() => {
    const checkDeliveryReview = async () => {
      if (!selectedOrder || selectedOrder.status !== 'shipped') return;

      // Bu sipariş için daha önce teslimat değerlendirmesi yapılmış mı kontrol et
      const { data } = await supabase
        .from('reviews')
        .select('id')
        .eq('order_id', selectedOrder.id)
        .eq('review_type', 'delivery')
        .maybeSingle();

      if (!data) {
        // Değerlendirme yapılmamış, göster
        setShowDeliveryReview(true);
      }
    };

    checkDeliveryReview();
  }, [selectedOrder]);

  const handleNotificationClick = (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
    }
    setShowNotifications(false);
    fetchUnreadNotifications();
  };

  const fetchQuotes = async () => {
    if (!customerEmail) return;
    
    setLoadingQuotes(true);
    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('customer_email', customerEmail)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) {
        setQuotes(data);
      }
    } catch (error) {
      console.error('Teklifler yüklenirken hata:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  useEffect(() => {
    if (showQuotes && customerEmail) {
      fetchQuotes();
    }
  }, [showQuotes, customerEmail]);

  const handleAcceptQuote = async (quoteId: string) => {
    if (!customerEmail) return;
    
    setAcceptingQuote(quoteId);
    try {
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'addQuoteToCart',
            quoteId,
            customerEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchQuotes();
        navigate('/cart');
      } else {
        alert(data.error || 'Teklif kabul edilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Teklif kabul edilirken hata:', error);
      alert('Teklif kabul edilirken bir hata oluştu');
    } finally {
      setAcceptingQuote(null);
    }
  };

  const getQuoteStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Taslak',
      sent: 'Gönderildi',
      accepted: 'Kabul Edildi',
      rejected: 'Reddedildi',
    };
    return labels[status] || status;
  };

  const getQuoteStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const isQuoteExpired = (validUntil: string | null) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const fetchRecommendations = async () => {
    setLoadingRecommendations(true);
    try {
      const { data, error } = await supabase
        .from('recommendations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setRecommendations(data);
    } catch (error) {
      console.error('Öneriler yüklenirken hata:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  useEffect(() => {
    if (showRecommendations && customerEmail) {
      fetchRecommendations();
    }
  }, [showRecommendations, customerEmail]);

  const isRecommendationInCart = (recommendationId: string) => {
    return cartItems.some((item) => item.recommendation_id === recommendationId);
  };

  const addRecommendationToCart = async (recommendationId: string) => {
    if (!customerEmail) return;
    
    setAddingRecommendationToCart(recommendationId);
    try {
      // Önce sepette var mı kontrol et
      const { data: existingCartItem } = await supabase
        .from('cart')
        .select('id')
        .eq('customer_email', customerEmail)
        .eq('recommendation_id', recommendationId)
        .maybeSingle();

      if (existingCartItem) {
        // Zaten sepette, direkt sepete yönlendir
        navigate('/cart');
        return;
      }

      // Orders tablosunda bu öneri için kayıt var mı kontrol et
      const { data: existingOrder } = await supabase
        .from('orders')
        .select('id')
        .eq('customer_email', customerEmail)
        .eq('recommendation_id', recommendationId)
        .maybeSingle();

      if (existingOrder) {
        // Order var, sadece sepete ekle
        const { error: cartError } = await supabase
          .from('cart')
          .insert({
            customer_email: customerEmail,
            order_id: existingOrder.id,
            recommendation_id: recommendationId,
          });

        if (cartError) throw cartError;
        await fetchCartItems();
        navigate('/cart');
        return;
      }

      // Hiç kayıt yok, edge function ile oluştur
      const response = await fetch(
        'https://ovsovpukgfcyrxhatwpk.supabase.co/functions/v1/admin-orders',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'addRecommendationToCart',
            recommendationId,
            customerEmail,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchCartItems();
        navigate('/cart');
      } else {
        alert(data.error || 'Sepete eklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Sepete eklenirken hata:', error);
      alert('Sepete eklenirken bir hata oluştu');
    } finally {
      setAddingRecommendationToCart(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col lg:flex-row font-sans">
      {/* Mobil Header */}
      <div className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img 
            src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png" 
            alt="Kartvizitler Logo" 
            className="h-9 w-auto object-contain"
          />
          <div className="h-5 w-px bg-gray-200"></div>
          <span className="text-gray-800 font-semibold text-sm tracking-tight">Sipariş Takip</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowNotifications(true)}
            className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer relative"
          >
            <i className="ri-notification-3-line text-lg"></i>
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse-soft">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
          >
            <i className={`text-lg ${sidebarOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Sol Sidebar */}
      <aside className={`${
        sidebarOpen ? 'fixed inset-0 z-50' : 'hidden'
      } lg:relative lg:flex w-full lg:w-[268px] bg-white border-r border-gray-100 flex-col lg:flex-shrink-0`}>
        <div className="p-5 border-b border-gray-100 hidden lg:block">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png" 
              alt="Kartvizitler Logo" 
              className="h-10 w-auto object-contain"
            />
            <div>
              <span className="text-gray-900 font-bold text-[15px] block leading-tight tracking-tight">Sipariş Takip</span>
              <span className="text-gray-400 text-[11px]">Müşteri Paneli</span>
            </div>
          </div>
        </div>

        {/* Mobil sidebar header */}
        <div className="lg:hidden p-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/4d025bfd0dded7d3f58fab80d1f8fe1d/58617c13764fc3aa761818d509f07204.png" 
              alt="Kartvizitler Logo" 
              className="h-9 w-auto object-contain"
            />
            <span className="text-gray-900 font-semibold text-sm">Menü</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-xl cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <div className="space-y-2 mb-5">
            <button
              onClick={() => {
                setShowOrderForm(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-sm shadow-emerald-200/50 text-sm font-medium"
            >
              <i className="ri-add-circle-line text-lg"></i>
              <span>Yeni Sipariş</span>
            </button>

            <button
              onClick={() => {
                setShowExistingOrderForm(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all cursor-pointer whitespace-nowrap text-sm font-medium"
            >
              <i className="ri-file-search-line text-lg"></i>
              <span>Mevcut Sipariş Takibi</span>
            </button>
          </div>

          <div className="space-y-0.5">
            <div className="px-3 py-2">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Siparişlerim</span>
            </div>
            <div className="flex items-center gap-3 px-3 py-2.5 bg-emerald-50 text-emerald-700 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                <i className="ri-list-check-2 text-base"></i>
              </div>
              <span className="text-sm font-medium">Tüm Siparişler</span>
              <span className="ml-auto bg-emerald-200/60 text-emerald-800 text-[11px] font-semibold px-2 py-0.5 rounded-full">{orders.length}</span>
            </div>
            
            {/* Bildirimler Butonu */}
            <button
              onClick={() => {
                setShowNotifications(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer relative group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-notification-3-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Bildirimler</span>
              {unreadNotifications > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>

            {/* Değerlendirmeler Butonu */}
            <button
              onClick={() => {
                setShowReviews(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-star-smile-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Değerlendirmeler</span>
            </button>

            {/* Anketler Butonu */}
            <button
              onClick={() => {
                setShowSurveys(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-survey-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Anketler</span>
            </button>
            
            {/* Sepet Butonu */}
            {getPaidOrdersCount() > 0 && (
              <button
                onClick={() => {
                  goToCart();
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer relative group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                  <i className="ri-shopping-cart-2-line text-base text-gray-500"></i>
                </div>
                <span className="text-sm font-medium">Sepetim</span>
                {cartItems.length > 0 && (
                  <span className="ml-auto bg-emerald-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                    {cartItems.length}
                  </span>
                )}
              </button>
            )}

            <div className="px-3 pt-4 pb-2">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Hesap</span>
            </div>
            
            {/* Hesaba Havale Butonu */}
            <button
              onClick={() => {
                navigate('/bank-transfer');
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-bank-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Hesaba Havale</span>
            </button>
            
            {/* Profil Butonu */}
            <button
              onClick={() => {
                setShowProfileModal(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-user-settings-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Profil Bilgilerim</span>
            </button>

            {/* Teklifler Butonu */}
            <button
              onClick={() => {
                setShowQuotes(true);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-price-tag-3-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Teklifler</span>
            </button>

            {/* Öneriler Butonu */}
            <button
              onClick={() => {
                setShowRecommendations(true);
                setShowQuotes(false);
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl transition-all cursor-pointer group"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
                <i className="ri-lightbulb-line text-base text-gray-500"></i>
              </div>
              <span className="text-sm font-medium">Öneriler</span>
            </button>
          </div>
        </nav>

        <div className="p-3 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl bg-gray-50/80">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-white font-semibold text-xs">
                {customerEmail?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-800 text-xs font-medium truncate">
                {customerEmail}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-all cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50">
              <i className="ri-logout-box-r-line text-base"></i>
            </div>
            <span className="text-sm font-medium">Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Ana İçerik */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Sipariş Listesi */}
        <div className="w-full lg:w-[356px] bg-white border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col max-h-[40vh] lg:max-h-none lg:flex-shrink-0">
          <div className="p-4 lg:p-5">
            <div className="flex items-center justify-between mb-3.5">
              <h2 className="text-gray-900 font-bold text-base lg:text-[17px] tracking-tight">Siparişlerim</h2>
              <span className="text-[11px] text-gray-400 font-medium bg-gray-50 px-2.5 py-1 rounded-full">{orders.length} sipariş</span>
            </div>
            <div className="relative">
              <i className="ri-search-2-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                placeholder="Sipariş ara..."
                className="w-full bg-gray-50/80 border border-gray-200/60 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none transition-all"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-9 h-9 border-[2.5px] border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
                <span className="text-gray-400 text-xs">Yükleniyor...</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-2-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-gray-800 font-semibold text-sm mb-1">Henüz sipariş yok</p>
                <p className="text-gray-400 text-xs mb-5">Hemen ilk siparişinizi oluşturun</p>
                <button
                  onClick={() => setShowOrderForm(true)}
                  className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  İlk Siparişi Oluştur
                </button>
              </div>
            ) : (
              <div className="px-2 pb-2 space-y-0.5">
                {orders.map((order) => (
                  <button
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`w-full p-3 text-left transition-all duration-200 cursor-pointer rounded-xl ${
                      selectedOrder?.id === order.id
                        ? 'bg-emerald-50/80 ring-1 ring-emerald-200/60'
                        : 'hover:bg-gray-50/80'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${getStatusBgColor(order.status)}`}>
                        <i className={`text-sm ${
                          order.status === 'pending' ? 'ri-time-line' :
                          order.status === 'designer_review' ? 'ri-user-search-line' :
                          order.status === 'design_in_progress' ? 'ri-pencil-ruler-2-line' :
                          order.status === 'awaiting_approval' ? 'ri-eye-line' :
                          order.status === 'sent_to_print' ? 'ri-printer-line' :
                          order.status === 'shipped' ? 'ri-truck-line' :
                          order.status === 'cancelled' ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'
                        }`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <h3 className="text-gray-900 font-semibold text-[13px] truncate pr-2">{order.order_title}</h3>
                          <span className="text-gray-400 text-[11px] flex-shrink-0">{formatTime(order.created_at)}</span>
                        </div>
                        <p className="text-gray-500 text-xs truncate mb-1.5">
                          {order.order_description || 'Açıklama yok'}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(order.status)}`}></span>
                          <span className="text-gray-500 text-[11px] font-medium">{getStatusLabel(order.status)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sipariş Detayı */}
        <div className="flex-1 bg-[#f8f9fb] flex flex-col overflow-hidden">
          {selectedOrder ? (
            <>
              <div className="px-4 lg:px-8 py-4 lg:py-5 border-b border-gray-100 bg-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 lg:gap-4">
                    <div className={`w-11 h-11 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center border shadow-sm ${getStatusBgColor(selectedOrder.status)}`}>
                      <i className={`text-xl lg:text-2xl ${
                        selectedOrder.status === 'pending' ? 'ri-time-line' :
                        selectedOrder.status === 'designer_review' ? 'ri-user-search-line' :
                        selectedOrder.status === 'design_in_progress' ? 'ri-pencil-ruler-2-line' :
                        selectedOrder.status === 'awaiting_approval' ? 'ri-eye-line' :
                        selectedOrder.status === 'sent_to_print' ? 'ri-printer-line' :
                        selectedOrder.status === 'shipped' ? 'ri-truck-line' :
                        selectedOrder.status === 'cancelled' ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'
                      }`}></i>
                    </div>
                    <div>
                      <h2 className="text-gray-900 font-bold text-lg lg:text-xl tracking-tight">{selectedOrder.order_title}</h2>
                      <p className="text-gray-400 text-xs mt-0.5 font-medium">#{selectedOrder.id.slice(0, 8).toUpperCase()} · {formatDate(selectedOrder.created_at)}</p>
                    </div>
                  </div>
                  <div className={`px-3.5 py-1.5 rounded-lg border text-xs font-semibold w-full sm:w-auto text-center ${getStatusBgColor(selectedOrder.status)}`}>
                    {getStatusLabel(selectedOrder.status)}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-5 lg:py-6">
                <div className="max-w-3xl space-y-5">
                  {/* Yönetici Notu */}
                  {selectedOrder.admin_note && (
                    <div className="bg-amber-50 rounded-xl p-4 lg:p-5 border border-amber-200/80 animate-fade-in">
                      <h3 className="text-amber-800 font-semibold mb-2 flex items-center gap-2 text-sm">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100">
                          <i className="ri-information-line text-amber-600 text-sm"></i>
                        </div>
                        Yönetici Notu
                      </h3>
                      <p className="text-amber-900/80 text-sm whitespace-pre-wrap leading-relaxed pl-9">{selectedOrder.admin_note}</p>
                    </div>
                  )}

                  {/* Fiyat ve Sepete Ekle/Satın Al Butonu */}
                  {selectedOrder.price && selectedOrder.price > 0 && !selectedOrder.paid && (
                    <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 rounded-xl p-5 border border-emerald-200/60 shadow-card animate-fade-in">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-gray-900 font-bold text-base lg:text-lg mb-0.5">Sipariş Tutarı</h3>
                          <p className="text-gray-500 text-xs">Sepete ekleyerek ödeme yapabilirsiniz</p>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl lg:text-4xl font-extrabold text-emerald-600">
                            {selectedOrder.price.toFixed(2)} <span className="text-lg font-bold">₺</span>
                          </div>
                        </div>
                      </div>
                      
                      {isInCart(selectedOrder.id) ? (
                        <button
                          onClick={goToCart}
                          className="w-full bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3.5 flex items-center justify-center gap-2 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer font-semibold text-sm"
                        >
                          <i className="ri-checkbox-circle-fill text-lg"></i>
                          <span>Sepetinizde - Satın Al</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => addToCart(selectedOrder.id)}
                          disabled={addingToCart}
                          className="w-full px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-md shadow-emerald-200/50 whitespace-nowrap disabled:opacity-50 text-sm"
                        >
                          {addingToCart ? (
                            <>
                              <i className="ri-loader-4-line animate-spin text-lg"></i>
                              Ekleniyor...
                            </>
                          ) : (
                            <>
                              <i className="ri-shopping-cart-2-line text-lg"></i>
                              Satın Al
                            </>
                          )}
                        </button>
                      )}
                      
                      <div className="flex items-center justify-center gap-2 mt-3 text-emerald-600 text-[11px]">
                        <i className="ri-shield-check-line text-sm"></i>
                        <span>Güvenli ödeme - PayTR ile korunuyor</span>
                      </div>
                    </div>
                  )}

                  {/* Ödeme Yapıldı Mesajı */}
                  {selectedOrder.paid && (
                    <div className="bg-emerald-50 rounded-xl p-4 lg:p-5 border border-emerald-200/60 animate-fade-in">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-fill text-xl text-emerald-600"></i>
                        </div>
                        <div>
                          <h3 className="text-emerald-800 font-semibold text-sm">Ödeme Tamamlandı</h3>
                          <p className="text-emerald-600 text-xs">Bu sipariş için ödeme alınmıştır</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onaylanan Tasarım Görseli */}
                  {selectedOrder.approved_design_url && (
                    <div className={`rounded-xl p-4 lg:p-5 border animate-fade-in ${
                      selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer 
                        ? 'bg-emerald-50/50 border-emerald-200/60' 
                        : 'bg-amber-50/50 border-amber-200/60'
                    }`}>
                      <h3 className={`font-semibold mb-3 lg:mb-4 flex items-center gap-2 text-sm ${
                      selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer 
                          ? 'text-emerald-800' 
                          : 'text-amber-800'
                      }`}>
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                      selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer 
                            ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                          <i className={`text-sm ${
                      selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer 
                              ? 'ri-checkbox-circle-fill text-emerald-600' 
                              : 'ri-time-fill text-amber-600'
                      }`}></i>
                        </div>
                        {selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer 
                          ? 'Onaylanan Tasarım' 
                          : 'Tasarım Onayınız Bekleniyor'}
                      </h3>
                      <div className="rounded-xl overflow-hidden border border-gray-200/60 bg-white">
                        <img
                          src={selectedOrder.approved_design_url}
                          alt="Tasarım"
                          className="w-full max-h-96 object-contain"
                        />
                      </div>
                      
                      {/* Müşteri Onay Butonu */}
                      {!selectedOrder.design_approved_by_admin && !selectedOrder.design_approved_by_customer && (
                        <div className="mt-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowApprovalModal(true)}
                              className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm whitespace-nowrap text-sm"
                            >
                              <i className="ri-checkbox-circle-line text-lg"></i>
                              Tasarımı Onayla
                            </button>
                            <button
                              onClick={() => setShowRevisionChat(true)}
                              className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm whitespace-nowrap text-sm"
                            >
                              <i className="ri-edit-2-line text-lg"></i>
                              Düzenleme Talep Et
                            </button>
                          </div>
                          <p className="text-amber-600 text-[11px] mt-2 text-center flex items-center justify-center gap-1">
                            <i className="ri-information-line"></i>
                            Tasarımı onaylamadan önce lütfen dikkatlice kontrol edin
                          </p>
                        </div>
                      )}

                      {/* Onay Durumu Mesajı */}
                      {(selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer) && (
                        <p className="text-emerald-600 text-[11px] mt-3 text-center flex items-center justify-center gap-1">
                          <i className="ri-printer-line"></i>
                          Bu tasarım baskıya alınmıştır
                        </p>
                      )}
                    </div>
                  )}

                  {/* Son Görüşmeler */}
                  {selectedOrder.approved_design_url && 
                   (selectedOrder.design_approved_by_admin || selectedOrder.design_approved_by_customer) && 
                   revisionMessages.length > 0 && (
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200/60 shadow-card animate-fade-in">
                      <h3 className="text-gray-900 font-semibold mb-3 lg:mb-4 flex items-center gap-2 text-sm">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
                          <i className="ri-chat-3-line text-orange-500 text-sm"></i>
                        </div>
                        Son Görüşmeler
                        <span className="ml-auto text-[11px] text-gray-400 font-medium bg-gray-50 px-2 py-0.5 rounded-full">{revisionMessages.length}</span>
                      </h3>
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                        {revisionMessages.map((msg) => (
                          <div key={msg.id} className="flex items-start gap-2.5">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              msg.sender_type === 'customer' 
                                ? 'bg-orange-50' 
                                : 'bg-emerald-50'
                            }`}>
                              <i className={`text-xs ${
                                msg.sender_type === 'customer' 
                                  ? 'ri-user-line text-orange-500' 
                                  : 'ri-team-line text-emerald-500'
                              }`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-[11px] font-semibold ${
                                  msg.sender_type === 'customer' 
                                    ? 'text-orange-600' 
                                    : 'text-emerald-600'
                                }`}>
                                  {msg.sender_type === 'customer' ? 'Siz' : 'XKartvizitler Ekibi'}
                                </span>
                                <span className="text-gray-300 text-[10px]">·</span>
                                <span className="text-gray-400 text-[10px]">{formatRevisionTime(msg.created_at)}</span>
                              </div>
                              <p className="text-gray-700 text-xs leading-relaxed break-words">{msg.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Kargo Takip Numarası */}
                  {selectedOrder.tracking_number && (
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200/60 shadow-card animate-fade-in">
                      <h3 className="text-gray-900 font-semibold mb-3 lg:mb-4 flex items-center gap-2 text-sm">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-50">
                          <i className="ri-truck-line text-sky-500 text-sm"></i>
                        </div>
                        Kargo Takip Bilgisi
                      </h3>
                      <div className="bg-sky-50/50 rounded-xl p-3 border border-sky-100">
                        {selectedOrder.shipping_company && (
                          <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-sky-100">
                            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-sky-100">
                              <i className="ri-building-2-line text-sm text-sky-600"></i>
                            </div>
                            <div>
                              <p className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Kargo Firması</p>
                              <p className="text-gray-900 font-semibold text-sm">{selectedOrder.shipping_company}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-sky-100">
                            <i className="ri-barcode-line text-xl text-sky-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Takip Numarası</p>
                            <p className="text-gray-900 font-mono text-base lg:text-lg font-bold">{selectedOrder.tracking_number}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-sky-500 text-[11px] mt-3 text-center flex items-center justify-center gap-1">
                        <i className="ri-information-line"></i>
                        Bu numara ile kargo firmasının web sitesinden gönderinizi takip edebilirsiniz
                      </p>
                    </div>
                  )}

                  {/* Durum Timeline */}
                  <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200/60 shadow-card animate-fade-in">
                    <h3 className="text-gray-900 font-semibold mb-4 lg:mb-5 flex items-center gap-2 text-sm">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
                        <i className="ri-route-line text-emerald-500 text-sm"></i>
                      </div>
                      Sipariş Durumu
                    </h3>
                    <div className="relative pl-1">
                      {/* Vertical line */}
                      <div className="absolute left-[14px] top-3 bottom-3 w-0.5 bg-gray-100 rounded-full"></div>
                      
                      <div className="space-y-0">
                        {[
                          { key: 'pending', icon: 'ri-time-line', title: 'Tasarım Bilgileriniz Bekleniyor', desc: 'Sipariş alındı' },
                          { key: 'designer_review', icon: 'ri-user-search-line', title: 'Grafiker Kontrolünde', desc: 'Tasarım inceleniyor' },
                          { key: 'design_in_progress', icon: 'ri-pencil-ruler-2-line', title: 'Tasarım Hazırlanıyor', desc: 'Grafiker çalışıyor' },
                          { key: 'awaiting_approval', icon: 'ri-eye-line', title: 'Tasarım Onayınız Bekleniyor', desc: 'Onay için hazır' },
                          { key: 'sent_to_print', icon: 'ri-printer-line', title: 'Üretimde', desc: 'Üretim aşamasında' },
                          { key: 'shipped', icon: 'ri-truck-line', title: 'Ürün Kargolandı', desc: 'Teslimata çıktı' },
                        ].map((step) => {
                          const completed = isStepCompleted(step.key, selectedOrder.status);
                          const isCurrent = step.key === selectedOrder.status;
                          return (
                            <div key={step.key} className="flex items-center gap-3 relative py-2.5">
                              <div className={`w-[28px] h-[28px] rounded-full flex items-center justify-center flex-shrink-0 z-10 transition-all duration-300 ${
                                completed
                                  ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                                  : isCurrent
                                    ? 'bg-white border-2 border-emerald-400 text-emerald-500 shadow-sm'
                                    : 'bg-gray-100 text-gray-400'
                              }`}>
                                {completed ? (
                                  <i className="ri-check-line text-xs font-bold"></i>
                                ) : (
                                  <i className={`${step.icon} text-[11px]`}></i>
                                )}
                              </div>
                              <div className="flex-1">
                                <p className={`text-xs font-semibold ${completed ? 'text-gray-900' : isCurrent ? 'text-emerald-600' : 'text-gray-400'}`}>
                                  {step.title}
                                </p>
                                <p className={`text-[11px] ${completed ? 'text-gray-500' : 'text-gray-400'}`}>{step.desc}</p>
                              </div>
                              {isCurrent && (
                                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse-soft flex-shrink-0"></span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Sipariş Bilgileri */}
                  <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200/60 shadow-card animate-fade-in">
                    <h3 className="text-gray-900 font-semibold mb-3 lg:mb-4 flex items-center gap-2 text-sm">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50">
                        <i className="ri-file-list-3-line text-gray-500 text-sm"></i>
                      </div>
                      Sipariş Detayları
                    </h3>
                    <div className="space-y-3 lg:space-y-4">
                      <div>
                        <label className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Sipariş Başlığı</label>
                        <p className="text-gray-900 mt-0.5 text-sm font-medium">{selectedOrder.order_title}</p>
                      </div>
                      {selectedOrder.order_description && (
                        <div>
                          <label className="text-gray-400 text-[10px] uppercase tracking-wider font-semibold">Açıklama</label>
                          <p className="text-gray-700 mt-0.5 whitespace-pre-wrap text-sm leading-relaxed">{selectedOrder.order_description}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {recommendations.map((rec) => {
                          return (
                            <div
                              key={rec.id}
                              className="bg-white border border-gray-200/60 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden animate-fade-in"
                            >
                              {rec.image_url ? (
                                <div
                                  className="w-full h-44 bg-gray-50 overflow-hidden cursor-zoom-in relative group"
                                  onClick={() => setLightboxImage(rec.image_url)}
                                >
                                  <img
                                    src={rec.image_url}
                                    alt={rec.title}
                                    className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                    <div className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                      <i className="ri-zoom-in-line text-gray-700 text-base"></i>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-44 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
                                  <i className="ri-lightbulb-line text-4xl text-amber-300"></i>
                                </div>
                              )}
                              <div className="p-4">
                                <h3 className="text-gray-900 font-semibold text-sm mb-1 line-clamp-2">{rec.title}</h3>
                                {rec.description && (
                                  <p className="text-gray-500 text-xs mb-3 line-clamp-2 leading-relaxed">{rec.description}</p>
                                )}
                                <div className="flex items-center justify-between">
                                  <span className="text-emerald-600 font-bold text-lg">{rec.price.toFixed(2)} ₺</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Dosyalar */}
                  {selectedOrder.file_urls && selectedOrder.file_urls.length > 0 && (
                    <div className="bg-white rounded-xl p-4 lg:p-5 border border-gray-200/60 shadow-card animate-fade-in">
                      <h3 className="text-gray-900 font-semibold mb-3 lg:mb-4 flex items-center gap-2 text-sm">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50">
                          <i className="ri-attachment-2 text-gray-500 text-sm"></i>
                        </div>
                        Yüklenen Dosyalar
                        <span className="ml-auto text-[11px] text-gray-400 font-medium bg-gray-50 px-2 py-0.5 rounded-full">{selectedOrder.file_urls.length}</span>
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedOrder.file_urls.map((url, index) => (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-gray-50/80 hover:bg-gray-100 border border-gray-200/60 rounded-xl transition-all cursor-pointer group"
                          >
                            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center border border-gray-200/60 flex-shrink-0">
                              <i className="ri-file-line text-base text-gray-500"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-800 text-xs font-medium truncate">Dosya {index + 1}</p>
                              <p className="text-gray-400 text-[11px]">Görüntülemek için tıkla</p>
                            </div>
                            <i className="ri-external-link-line text-gray-300 group-hover:text-emerald-500 transition-colors flex-shrink-0 text-sm"></i>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-cursor-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-gray-500 text-sm font-medium">Detayları görmek için bir sipariş seçin</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showOrderForm && (
        <OrderForm
          userEmail={customerEmail || ''}
          userName={customerEmail || ''}
          onClose={() => setShowOrderForm(false)}
          onSuccess={() => fetchOrders()}
        />
      )}

      {showExistingOrderForm && (
        <ExistingOrderForm
          userEmail={customerEmail || ''}
          userName={customerEmail || ''}
          onClose={() => setShowExistingOrderForm(false)}
          onSuccess={() => fetchOrders()}
        />
      )}

      {showProfileModal && (
        <ProfileModal
          customerEmail={customerEmail || ''}
          onClose={() => setShowProfileModal(false)}
        />
      )}

      {/* Tasarım Onay Popup */}
      {showApprovalModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-gray-900 font-bold text-lg flex items-center gap-2.5">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-lg"></i>
                </div>
                Tasarımı Onayla
              </h3>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-amber-600"></i>
                </div>
                <div>
                  <p className="text-amber-800 font-semibold text-sm mb-2">
                    &quot;Tasarımı Onayla&quot; butonuna tıklamanız halinde;
                  </p>
                  <ul className="space-y-2 text-amber-700 text-xs">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                      <span>Tasarımdaki yazım, imla, tarih, iletişim bilgileri ve içerik hatalarının tarafınızca kontrol edildiği,</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                      <span>Olası yazım yanlışlarının ve içerik hatalarının müşteri tarafından kabul edildiği,</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                      <span>Onay sonrası yapılacak değişiklik taleplerinin dikkate alınmayacağı,</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-amber-500 mt-0.5 flex-shrink-0"></i>
                      <span>Baskı sürecine geçen ürünlerde iptal ve revizyon yapılamayacağı</span>
                    </li>
                  </ul>
                  <p className="text-amber-800 font-semibold text-xs mt-3">
                    tarafınızca kabul edilmiş sayılır.
                  </p>
                </div>
              </div>
            </div>

            {/* Tasarım Önizleme */}
            {selectedOrder.approved_design_url && (
              <div className="bg-gray-50 border border-gray-200/60 rounded-xl p-3 mb-5">
                <p className="text-gray-400 text-[10px] mb-2 text-center uppercase tracking-wider font-semibold">Onaylanacak Tasarım</p>
                <img
                  src={selectedOrder.approved_design_url}
                  alt="Tasarım"
                  className="w-full max-h-48 object-contain rounded-lg"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                disabled={approvingDesign}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50 text-sm whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={handleApproveDesign}
                disabled={approvingDesign}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap text-sm shadow-sm"
              >
                {approvingDesign ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Onaylanıyor...
                  </>
                ) : (
                  <>
                    <i className="ri-checkbox-circle-line"></i>
                    Tasarımı Onaylıyorum
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasarım Onay Başarı Mesajı */}
      {showApprovalSuccess && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-md w-full p-8 text-center animate-scale-in">
            <div className="w-20 h-20 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <i className="ri-checkbox-circle-fill text-4xl text-emerald-500"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-2xl mb-2">Tasarım Onaylandı!</h3>
            <p className="text-gray-500 text-sm">Siparişiniz üretim aşamasına geçiyor...</p>
          </div>
        </div>
      )}

      {/* Mini Değerlendirme Modalı */}
      {showMiniReview && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-lg w-full p-6 animate-scale-in">
            <h3 className="text-gray-900 font-bold text-lg mb-1 text-center">
              Hizmetimiz hakkında değerlendirme yapabilirsiniz
            </h3>
            <p className="text-gray-500 text-xs mb-6 text-center">
              Deneyiminizi paylaşarak hizmet kalitemizi geliştirmemize yardımcı olabilirsiniz
            </p>

            {/* Yıldız Puanlama */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-3 text-center">
                Tasarım onay sürecinden memnun kaldınız mi? <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setMiniReviewRating(star)}
                    className="p-1.5 cursor-pointer transition-all hover:scale-125"
                  >
                    <i className={`text-3xl ${
                      star <= miniReviewRating
                        ? 'ri-star-fill text-amber-400'
                        : 'ri-star-line text-gray-300'
                    }`}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Yorum (Opsiyonel) */}
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Kısa bir yorum eklemek ister misiniz?
                <span className="text-gray-400 font-normal text-xs">(Opsiyonel)</span>
              </label>
              <textarea
                value={miniReviewComment}
                onChange={(e) => setMiniReviewComment(e.target.value)}
                rows={3}
                maxLength={300}
                placeholder="Her şey çok netti, hızlı ilerledi."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none resize-none transition-all"
              />
              <p className="text-gray-400 text-[11px] mt-1 text-right">{miniReviewComment.length}/300</p>
            </div>

            {/* Butonlar */}
            <div className="flex gap-3">
              <button
                onClick={handleSkipMiniReview}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap"
              >
                Atla
              </button>
              <button
                onClick={handleSaveMiniReview}
                disabled={savingMiniReview || miniReviewRating === 0}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap shadow-sm"
              >
                {savingMiniReview ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teslimat Sonrası Değerlendirme Modalı */}
      {showDeliveryReview && selectedOrder?.status === 'shipped' && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full p-6 my-8 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-3xl text-emerald-500"></i>
              </div>
              <h3 className="text-gray-900 font-bold text-xl mb-1">Siparişiniz Teslim Edildi</h3>
              <p className="text-gray-500 text-xs">
                Siparişiniz başarıyla teslim edilmiştir.<br />
                Deneyiminizi bizimle paylaşarak hizmet kalitemizi geliştirmemize yardımcı olabilirsiniz.
              </p>
            </div>

            <div className="space-y-5">
              {/* Genel Memnuniyet */}
              <div className="text-center">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Genel Memnuniyet <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setDeliveryReviewData(prev => ({ ...prev, generalRating: star }))}
                      className="p-1.5 cursor-pointer transition-all hover:scale-125"
                    >
                      <i className={`text-3xl ${
                        star <= deliveryReviewData.generalRating
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* Baskı Kalitesi */}
              <div className="text-center">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Baskı Kalitesi <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setDeliveryReviewData(prev => ({ ...prev, printQualityRating: star }))}
                      className="p-1.5 cursor-pointer transition-all hover:scale-125"
                    >
                      <i className={`text-3xl ${
                        star <= deliveryReviewData.printQualityRating
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* Hız */}
              <div className="text-center">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Hız <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setDeliveryReviewData(prev => ({ ...prev, speedRating: star }))}
                      className="p-1.5 cursor-pointer transition-all hover:scale-125"
                    >
                      <i className={`text-3xl ${
                        star <= deliveryReviewData.speedRating
                          ? 'ri-star-fill text-amber-400'
                          : 'ri-star-line text-gray-300'
                      }`}></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* Yorum (Opsiyonel) */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Yorumunuz <span className="text-gray-400 font-normal text-xs">(Opsiyonel)</span>
                </label>
                <textarea
                  value={deliveryReviewData.comment}
                  onChange={(e) => setDeliveryReviewData(prev => ({ ...prev, comment: e.target.value }))}
                  rows={4}
                  maxLength={500}
                  placeholder="Baskı kalitesi ve teslim süreci beklentimin üzerindeydi."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 outline-none resize-none transition-all"
                />
                <p className="text-gray-400 text-[11px] mt-1 text-right">{deliveryReviewData.comment.length}/500</p>
              </div>
            </div>

            {/* Butonlar */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeliveryReview(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer text-sm whitespace-nowrap"
              >
                Daha Sonra
              </button>
              <button
                onClick={handleSaveDeliveryReview}
                disabled={savingDeliveryReview || deliveryReviewData.generalRating === 0 || deliveryReviewData.printQualityRating === 0 || deliveryReviewData.speedRating === 0}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-colors cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap shadow-sm"
              >
                {savingDeliveryReview ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Değerlendirmeyi Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifications && (
        <NotificationsPage
          customerEmail={customerEmail || ''}
          onClose={() => {
            setShowNotifications(false);
            fetchUnreadNotifications();
          }}
          onNotificationClick={handleNotificationClick}
        />
      )}

      {showReviews && (
        <ReviewsPage
          customerEmail={customerEmail || ''}
          onClose={() => setShowReviews(false)}
        />
      )}

      {showSurveys && (
        <SurveysPanel
          customerEmail={customerEmail || ''}
          onClose={() => setShowSurveys(false)}
        />
      )}

      {showRevisionChat && selectedOrder && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-gray-900 font-bold text-base flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50">
                  <i className="ri-edit-2-line text-orange-500"></i>
                </div>
                Tasarım Düzenleme Talebi
              </h3>
              <button
                onClick={() => setShowRevisionChat(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4">
              <DesignRevisionChat
                orderId={selectedOrder.id}
                customerEmail={customerEmail}
                customerName={selectedOrder.customer_name}
                onClose={() => setShowRevisionChat(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showQuotes && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-100 px-5 lg:px-8 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowQuotes(false)}
                className="w-8 h-8 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer lg:hidden"
              >
                <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
              </button>
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
                <i className="ri-price-tag-3-line text-base text-emerald-600"></i>
              </div>
              <div>
                <h1 className="text-gray-900 font-bold text-base">Tekliflerim</h1>
                <p className="text-gray-400 text-[11px]">{quotes.length} teklif</p>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 lg:p-6">
            {loadingQuotes ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-9 h-9 border-[2.5px] border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
                <span className="text-gray-400 text-xs">Yükleniyor...</span>
              </div>
            ) : quotes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-price-tag-3-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-gray-800 font-semibold text-sm mb-1">Henüz teklif yok</p>
                <p className="text-gray-400 text-xs">Size özel teklifler burada görünecektir</p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-4">
                {quotes.map((quote) => {
                  const expired = isQuoteExpired(quote.valid_until);
                  const canAccept = quote.status === 'sent' && !expired;
                  
                  return (
                    <div
                      key={quote.id}
                      className="bg-white border border-gray-200/60 rounded-xl shadow-card hover:shadow-soft transition-all p-5 animate-fade-in"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-gray-900 font-bold text-sm mb-1">{quote.title}</h3>
                          {quote.description && (
                            <p className="text-gray-500 text-xs mb-2">{quote.description}</p>
                          )}
                        </div>
                        <span className={`ml-3 px-2.5 py-1 text-[11px] font-semibold rounded-lg whitespace-nowrap ${getQuoteStatusColor(quote.status)}`}>
                          {getQuoteStatusLabel(quote.status)}
                        </span>
                      </div>

                      <div className="bg-gray-50/80 border border-gray-200/60 rounded-xl p-3.5 mb-3">
                        <div className="space-y-1.5">
                          {quote.items.map((item, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">{item.name} <span className="text-gray-400">x{item.quantity}</span></span>
                              <span className="text-gray-800 font-semibold">{item.total.toFixed(2)} ₺</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-gray-200 mt-2.5 pt-2 flex items-center justify-between">
                          <span className="text-gray-600 font-semibold text-xs">Toplam</span>
                          <span className="text-emerald-600 font-bold text-lg">{quote.total_price.toFixed(2)} ₺</span>
                        </div>
                      </div>

                      <div className="space-y-1.5 mb-3">
                        {quote.valid_until && (
                          <div className="flex items-center gap-2 text-[11px] text-gray-500">
                            <i className="ri-calendar-line text-sm"></i>
                            <span>
                              Geçerlilik: {new Date(quote.valid_until).toLocaleDateString('tr-TR')}
                              {expired && <span className="ml-2 text-red-500 font-semibold">(Süresi Doldu)</span>}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                          <i className="ri-time-line text-sm"></i>
                          <span>{new Date(quote.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>

                      {quote.status === 'accepted' ? (
                        <div className="bg-emerald-50 border border-emerald-200/60 rounded-xl p-3 text-center">
                          <i className="ri-checkbox-circle-fill text-emerald-500 text-base mr-1.5"></i>
                          <span className="text-emerald-700 text-xs font-semibold">Bu teklif kabul edildi ve siparişe dönüştürüldü</span>
                        </div>
                      ) : expired ? (
                        <div className="bg-red-50 border border-red-200/60 rounded-xl p-3 text-center">
                          <i className="ri-time-line text-red-500 text-base mr-1.5"></i>
                          <span className="text-red-600 text-xs font-semibold">Bu teklifin geçerlilik süresi doldu</span>
                        </div>
                      ) : canAccept ? (
                        <button
                          onClick={() => handleAcceptQuote(quote.id)}
                          disabled={acceptingQuote === quote.id}
                          className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap text-sm shadow-sm shadow-emerald-200/50"
                        >
                          {acceptingQuote === quote.id ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              İşleniyor...
                            </>
                          ) : (
                            <>
                              <i className="ri-shopping-cart-2-line text-lg"></i>
                              Teklifi Kabul Et ve Sepete Ekle
                            </>
                          )}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Öneriler Paneli */}
      {showRecommendations && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-elevated max-w-4xl w-full max-h-[90vh] flex flex-col animate-scale-in">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl">
                  <i className="ri-lightbulb-line text-base text-amber-500"></i>
                </div>
                <div>
                  <h2 className="text-gray-900 font-bold text-base">Önerilen Ürünler</h2>
                  <p className="text-gray-400 text-[11px]">Size özel ürün önerileri</p>
                </div>
              </div>
              <button
                onClick={() => setShowRecommendations(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {loadingRecommendations ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-9 h-9 border-[2.5px] border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-xs">Yükleniyor...</span>
                </div>
              ) : recommendations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
                    <i className="ri-lightbulb-line text-2xl text-gray-300"></i>
                  </div>
                  <p className="text-gray-800 font-semibold text-sm mb-1">Henüz öneri yok</p>
                  <p className="text-gray-400 text-xs">Size özel ürün önerileri burada görünecektir</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendations.map((rec) => {
                    return (
                      <div
                        key={rec.id}
                        className="bg-white border border-gray-200/60 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden animate-fade-in"
                      >
                        {rec.image_url ? (
                          <div
                            className="w-full h-44 bg-gray-50 overflow-hidden cursor-zoom-in relative group"
                            onClick={() => setLightboxImage(rec.image_url)}
                          >
                            <img
                              src={rec.image_url}
                              alt={rec.title}
                              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                              <div className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                <i className="ri-zoom-in-line text-gray-700 text-base"></i>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-44 bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
                            <i className="ri-lightbulb-line text-4xl text-amber-300"></i>
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="text-gray-900 font-semibold text-sm mb-1 line-clamp-2">{rec.title}</h3>
                          {rec.description && (
                            <p className="text-gray-500 text-xs mb-3 line-clamp-2 leading-relaxed">{rec.description}</p>
                          )}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-emerald-600 font-bold text-lg">{rec.price.toFixed(2)} ₺</span>
                          </div>
                          {isRecommendationInCart(rec.id) ? (
                            <button
                              onClick={goToCart}
                              className="w-full bg-emerald-50 border-2 border-emerald-300 rounded-xl p-2.5 flex items-center justify-center gap-2 text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer font-semibold text-xs"
                            >
                              <i className="ri-checkbox-circle-fill text-base"></i>
                              <span className="whitespace-nowrap">Sepetinizde - Satın Al</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => addRecommendationToCart(rec.id)}
                              disabled={addingRecommendationToCart === rec.id}
                              className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-md shadow-emerald-200/50 whitespace-nowrap disabled:opacity-50 text-xs"
                            >
                              {addingRecommendationToCart === rec.id ? (
                                <>
                                  <i className="ri-loader-4-line animate-spin text-base"></i>
                                  Ekleniyor...
                                </>
                              ) : (
                                <>
                                  <i className="ri-shopping-cart-2-line text-base"></i>
                                  Satın Al
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-4 -right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-gray-100 transition-colors z-10"
            >
              <i className="ri-close-line text-gray-700 text-lg"></i>
            </button>
            <img
              src={lightboxImage}
              alt="Ürün görseli"
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
